import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle, Printer, LinkSimple, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth, login } from "@/context/AuthContext";
import { PageHead } from "@/components/Layout";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Logo } from "@/components/Logo";
import { useT } from "@/i18n";

const LEGAL = {
  terms: ["Terms of Service", ["HostScribe provides AI-assisted writing tools and digital templates for short-term rental hosts.", "You are responsible for checking every AI-generated text before publishing it, including amenities and legal statements.", "Subscriptions renew monthly and can be cancelled at any time; access continues until the end of the paid period.", "Digital templates are licensed for use with your own properties and may not be resold.", "We do not connect to, scrape or act on behalf of Airbnb, Booking.com or any other platform."]],
  privacy: ["Privacy & GDPR", ["We store your email, saved properties and generated texts in an EU-hosted database.", "Email newsletters use double opt-in. You can unsubscribe from any email.", "Payments are processed by Stripe; we never see your card details.", "AI processing is done by our model providers on a no-training basis. Do not paste guests' personal data.", "You can request export or deletion of your data at any time via the Help center."]],
  imprint: ["Imprint", ["HostScribe is a product of KINGBORNSYSTEM, Terzigno (NA), Italy.", "Contact: WhatsApp +39 392 646 3229 · hello@hostscribe.com", "VAT number: IT01690820889", "Responsible for content: KINGBORNSYSTEM."]],
};

const LEGAL_IT = {
  terms: ["Termini di servizio", ["HostScribe offre strumenti di scrittura assistiti dall'AI e template digitali per chi gestisce affitti brevi.", "Sei responsabile di verificare ogni testo generato dall'AI prima di pubblicarlo, compresi servizi e affermazioni legali.", "Gli abbonamenti si rinnovano ogni mese e si possono disdire in qualsiasi momento; l'accesso resta attivo fino alla fine del periodo pagato.", "I template digitali sono concessi in licenza per l'uso sulle tue strutture e non possono essere rivenduti.", "Non ci colleghiamo, non raccogliamo dati e non agiamo per conto di Airbnb, Booking.com o altre piattaforme."]],
  privacy: ["Privacy e GDPR", ["Conserviamo la tua email, le strutture salvate e i testi generati in un database ospitato nell'UE.", "La newsletter usa il doppio consenso (double opt-in). Puoi disiscriverti da ogni email.", "I pagamenti sono gestiti da Stripe; non vediamo mai i dati della tua carta.", "L'elaborazione AI avviene presso i nostri fornitori di modelli senza addestramento sui tuoi dati. Non incollare dati personali degli ospiti.", "Puoi chiedere l'esportazione o la cancellazione dei tuoi dati in qualsiasi momento dal Centro assistenza."]],
  imprint: ["Note legali", ["HostScribe è un prodotto KINGBORNSYSTEM, Terzigno (NA), Italia.", "Contatti: WhatsApp +39 392 646 3229 · hello@hostscribe.com", "Partita IVA: 01690820889", "Responsabile dei contenuti: KINGBORNSYSTEM."]],
};

export function LegalPage() {
  const { t, lang } = useT();
  const { doc } = useParams();
  const L = lang === "it" ? LEGAL_IT : LEGAL;
  const [title, paras] = L[doc] || L.terms;
  return (
    <div data-testid={`legal-page-${doc}`}>
      <PageHead eyebrow="Legal" title={title} sub="Last updated 1 June 2026" />
      <section className="max-w-3xl px-6 lg:px-10 mx-auto lg:mx-0 lg:ml-[max(1.5rem,calc((100vw-80rem)/2+2.5rem))] pb-24 space-y-4 text-gray-800">
        {paras.map((p, i) => <p key={i}>{p}</p>)}
      </section>
    </div>
  );
}

export function HelpPage() {
  const { t, lang } = useT();
  const [faq, setFaq] = useState([]);
  useEffect(() => { api.get("/help").then((r) => setFaq(r.data)); }, [lang]);
  return (
    <div data-testid="help-page">
      <PageHead eyebrow="Help center" title="Answers for busy hosts" sub="Can't find it? Email hello@hostscribe.com and a real host replies within one working day." />
      <section className="max-w-3xl px-6 lg:px-10 mx-auto lg:mx-0 lg:ml-[max(1.5rem,calc((100vw-80rem)/2+2.5rem))] pb-24">
        <Accordion type="single" collapsible className="card px-6 bg-white">
          {faq.map((f, i) => (
            <AccordionItem key={i} value={`q${i}`} className="border-[#E2DCD0]">
              <AccordionTrigger className="text-left font-display text-lg text-[#0F4C5C]" data-testid={`faq-trigger-${i}`}>{f.q}</AccordionTrigger>
              <AccordionContent className="text-gray-700">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </div>
  );
}

export function LoginPage() {
  const { t } = useT();
  const { user } = useAuth();
  return (
    <div className="grain min-h-[70vh] max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-2 gap-12 items-center" data-testid="login-page">
      <div>
        <h1 className="text-4xl sm:text-5xl text-[#0F4C5C]">{t("Welcome back, host.")}</h1>
        <p className="mt-4 text-gray-700 max-w-md">{t("Sign in to rewrite listings, send guest messages in 10 languages and build guidebooks.")}</p>
        {user ? <Link to="/app" className="btn-teal mt-8" data-testid="login-open-app-btn">{t("Open the app")}</Link>
          : <button onClick={login} className="btn-coral mt-8" data-testid="login-google-btn">{t("Continue with Google")}</button>}
      </div>
      <img src="https://images.unsplash.com/photo-1577614739132-81b2b774038c?auto=format&w=900" alt="Calm interior" className="rounded-2xl h-80 w-full object-cover border border-[#E2DCD0]" />
    </div>
  );
}

export function PaymentResult({ cancelled = false }) {
  const { t } = useT();
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [state, setState] = useState(cancelled ? "cancelled" : "checking");
  const [downloads, setDownloads] = useState([]);
  const sid = params.get("session_id");

  useEffect(() => {
    if (cancelled || !sid) return;
    let tries = 0, timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { setState("paid"); setDownloads(data.downloads || []); refresh(); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("failed"); return; }
      } catch { /* retry */ }
      if (++tries < 10) timer = setTimeout(poll, 2000); else setState("timeout");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sid, cancelled, refresh]);

  const msg = {
    checking: [t("Confirming your payment…"), t("This takes a few seconds.")],
    paid: [t("Payment confirmed. Thank you!"), t("Stripe emails your receipt. Plan upgrades are active right away; template downloads are below.")],
    failed: [t("Payment didn't go through"), t("No money was taken. You can try again any time.")],
    timeout: [t("Still confirming"), t("Check your email in a minute, or contact us from the Help center.")],
    cancelled: [t("Checkout cancelled"), t("No charge was made. Your cart is waiting when you're ready.")],
  }[state];
  const ok = state === "paid";
  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-left" data-testid="payment-result-page">
      {ok ? <CheckCircle size={56} weight="duotone" className="text-[#0F4C5C]" /> : state !== "checking" && <XCircle size={56} weight="duotone" className="text-[#E36414]" />}
      <h1 className="mt-4 text-4xl text-[#0F4C5C]" data-testid="payment-status-title">{msg[0]}</h1>
      <p className="mt-3 text-gray-700">{msg[1]}</p>
      {downloads.length > 0 && (
        <div className="mt-8 card p-6 bg-white" data-testid="payment-downloads">
          <p className="label">{t("Your downloads")}</p>
          <ul className="space-y-2">{downloads.map((d) => (
            <li key={d.file_id}><a href={`${process.env.REACT_APP_BACKEND_URL}${d.url}`} className="btn-teal text-sm !py-2" data-testid={`download-${d.file_id}`}><DownloadSimple size={16} /> {d.name}</a></li>
          ))}</ul>
          <p className="text-xs text-gray-500 mt-3">{t("Bookmark this page to download again. Signed in with the same email? Your files are also under “My templates” in the app.")}</p>
        </div>
      )}
      <div className="mt-8 flex gap-3"><Link to="/app" className="btn-teal" data-testid="payment-open-app-btn">{t("Open the app")}</Link><Link to="/templates" className="btn-ghost">{t("Templates")}</Link></div>
    </div>
  );
}

export function PublicGuidebook() {
  const { t } = useT();
  const { slug } = useParams();
  const [g, setG] = useState(null);
  useEffect(() => { api.get(`/public/guidebook/${slug}`).then((r) => setG(r.data)).catch(() => setG(false)); }, [slug]);
  if (g === false) return <p className="p-16" data-testid="public-guidebook-missing">{t("This guidebook link is not valid.")}</p>;
  if (!g) return <div className="p-16"><div className="skeleton h-64 max-w-3xl" /></div>;
  return (
    <div className="min-h-screen bg-[#FCFAF8]" data-testid="public-guidebook-page">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="flex justify-between items-center no-print">
          <Logo size={24} />
          <div className="flex gap-2">
            <button onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success(t("Link copied")); }} className="btn-ghost text-sm !py-1.5" data-testid="public-guidebook-copy-btn"><LinkSimple size={16} /> {t("Copy link")}</button>
            <button onClick={() => window.print()} className="btn-teal text-sm !py-1.5" data-testid="public-guidebook-print-btn"><Printer size={16} /> {t("Save as PDF")}</button>
          </div>
        </div>
        <h1 className="mt-12 text-4xl sm:text-5xl text-[#0F4C5C]" data-testid="public-guidebook-title">{g.title}</h1>
        <p className="text-sm text-gray-500 mt-2">{g.input?.area}, {t(g.input?.country)} · {t(g.lang)}</p>
        {g.sections.map((s, i) => (
          <section key={i} className="mt-10 break-inside-avoid">
            <h2 className="text-2xl text-[#0F4C5C] border-b border-[#E2DCD0] pb-2">{s.heading}</h2>
            <ul className="mt-3 space-y-2 text-gray-800">{s.items.map((it, j) => <li key={j} className="pl-4 border-l-2 border-[#E36414]/50">{it}</li>)}</ul>
          </section>
        ))}
        <p className="mt-12 text-xs text-gray-500">{t("Places marked “verify” were suggested by AI; your host has been asked to confirm them.")}</p>
      </div>
    </div>
  );
}
